import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const { tf, lang, setLang } = useLang();
  const [mode, setMode] = useState("password"); // "social" | "password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const redirectTo = location.state?.from?.pathname || "/dashboard";

  const handleGoogle = () => {
    const redirectUrl = `${window.location.origin}/auth-callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [twofa, setTwofa] = useState(null); // {challenge_id, methods}
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const finishLogin = (data) => {
    setUser(data);
    if (data.recovery_codes_remaining !== undefined) {
      toast.warning(lang === "en" ? `Recovery code used. You have ${data.recovery_codes_remaining} left.` : `Code de récupération utilisé. Il vous en reste ${data.recovery_codes_remaining}.`);
    } else {
      toast.success(tf("Bienvenue !"));
    }
    const inviteToken = (() => { try { return localStorage.getItem("athlynx_invite_token"); } catch { return null; } })();
    if (data.must_change_password) {
      toast.warning(tf("Veuillez changer votre mot de passe initial."));
      navigate("/settings/security", { replace: true });
      return;
    }
    navigate(inviteToken ? "/join" : redirectTo, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
        remember_me: remember,
      });
      if (r.data.two_factor_required) {
        setTwofa({ challenge_id: r.data.challenge_id, methods: r.data.methods || [] });
        return;
      }
      finishLogin(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tf("Connexion impossible"));
    } finally {
      setBusy(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify", {
        challenge_id: twofa.challenge_id, code: code.trim(), use_recovery: useRecovery,
      });
      finishLogin(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tf("Code invalide"));
      if (err?.response?.status === 429) { setTwofa(null); setCode(""); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0C12] flex" data-testid="login-page">
      {/* === LEFT PANEL — brand & promise === */}
      <aside
        className={cn(
          "hidden md:flex relative flex-col justify-between",
          "w-[44%] lg:w-[48%] xl:w-[44%] p-10 lg:p-14 overflow-hidden"
        )}
        aria-hidden="false"
      >
        {/* Gradient backdrop (red→orange premium) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 20% 0%, #B91D14 0%, #6E1209 35%, #1B0606 70%, #0A0C12 100%)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 mix-blend-overlay opacity-60"
          style={{
            background:
              "radial-gradient(circle at 75% 80%, rgba(255,140,40,0.5), transparent 55%)",
          }}
          aria-hidden
        />
        {/* Subtle grain */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>\")",
          }}
          aria-hidden
        />

        <header className="relative z-10 flex items-center gap-3">
          <img
            src="/athlynx-logo-white.svg"
            alt="Athlynx"
            className="h-9 w-9 select-none"
            draggable={false}
          />
          <div className="text-white/95 font-semibold tracking-tight text-lg">Athlynx</div>
        </header>

        <div className="relative z-10 max-w-md space-y-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">
            {tf("Plateforme de gestion sportive")}
          </div>
          <h1 className="text-3xl lg:text-4xl xl:text-5xl font-semibold text-white leading-[1.05] tracking-tight">
            {tf("Votre réseau.")}
            <br />
            {tf("Vos athlètes.")}
            <br />
            <span className="text-white/70">{tf("Votre temps.")}</span>
          </h1>
          <p className="text-white/70 text-[15px] leading-relaxed max-w-prose">
            {tf("Athlynx centralise vos opportunités, vos échanges et la gestion de vos athlètes. Pipeline, calendrier, relations et dossiers réunis dans un seul outil.")}
          </p>
        </div>

        <footer className="relative z-10 flex items-center gap-2 text-[12px] text-white/55">
          <ShieldCheck className="h-3.5 w-3.5" />
          {tf("Sécurisé. Fiable. Conçu pour les agents.")}
        </footer>
      </aside>

      {/* === RIGHT PANEL — form === */}
      <main className="relative flex-1 flex flex-col items-center justify-center px-5 py-10 md:px-10 bg-[#080D15]">
        <div
          role="group"
          aria-label={tf("Choisir la langue")}
          data-testid="login-lang-switch"
          className="absolute top-4 right-4 md:top-5 md:right-5 z-20 inline-flex items-center rounded-lg border border-white/[0.12] bg-white/[0.04] p-0.5"
        >
          {["fr", "en"].map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              data-testid={`login-lang-${l}`}
              className={
                lang === l
                  ? "h-8 min-w-[38px] px-2.5 rounded-md bg-[#F5F7FA] text-[#0A0C12] text-xs font-semibold tracking-wide transition-colors"
                  : "h-8 min-w-[38px] px-2.5 rounded-md text-[#8E9CB2] hover:text-white text-xs font-medium tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40"
              }
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="w-full max-w-sm">
          {/* Logo on mobile */}
          <div className="md:hidden flex items-center gap-2 mb-8">
            <img src="/athlynx-logo-white.svg" alt="Athlynx" className="h-7 w-7" />
            <span className="text-base font-semibold tracking-tight text-white">Athlynx</span>
          </div>

          <header className="mb-7 space-y-1">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#F5F7FA]">
              {tf("Bienvenue sur Athlynx")}
            </h2>
            <p className="text-sm text-[#9AA9BF]">
              {tf("Connectez-vous à votre compte ou créez-en un nouveau.")}
            </p>
          </header>

          {/* Social buttons */}
          {!twofa && (<>
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              className={cn(
                "group w-full flex items-center justify-center gap-2.5 h-11 rounded-xl",
                "bg-white text-[#0A0C12] font-medium text-sm",
                "hover:bg-white/90 active:scale-[0.98] transition-all duration-150",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#080D15]",
                "disabled:opacity-50"
              )}
              data-testid="login-google-btn"
            >
              <GoogleIcon />
              {tf("Continuer avec Google")}
            </button>
            <button
              type="button"
              disabled
              className={cn(
                "w-full flex items-center justify-center gap-2.5 h-11 rounded-xl",
                "bg-white/[0.05] border border-white/[0.14] text-[#AEBDD4] font-medium text-sm",
                "cursor-not-allowed"
              )}
              data-testid="login-apple-btn"
              title={tf("Bientôt disponible")}
            >
              <AppleIcon />
              {tf("Continuer avec iCloud")}
              <span className="ml-1 text-[10px] uppercase tracking-wider text-[#7C8CA5]">
                {tf("Bientôt")}
              </span>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/[0.12]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#8B9BB4]">
              {tf("ou")}
            </span>
            <div className="flex-1 h-px bg-white/[0.12]" />
          </div>
          </>)}

          {/* Email/password form */}
          {twofa ? (
            <form onSubmit={handleVerify2fa} className="space-y-4" data-testid="twofa-form">
              <div>
                <h2 className="text-lg font-semibold text-white">{tf("Vérifiez votre identité")}</h2>
                <p className="text-sm text-white/60 mt-1">
                  {useRecovery
                    ? tf("Saisissez l'un de vos codes de récupération (format XXXX-XXXX).")
                    : twofa.methods.includes("totp")
                      ? tf("Saisissez le code à 6 chiffres de votre application d'authentification.")
                      : tf("Nous avons envoyé un code à 6 chiffres à votre adresse email.")}
                </p>
              </div>
              <input
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={useRecovery ? "XXXX-XXXX" : "000000"}
                inputMode={useRecovery ? "text" : "numeric"}
                maxLength={useRecovery ? 9 : 6}
                className="w-full h-12 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-center text-xl tracking-[0.4em] text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                data-testid="twofa-code-input"
              />
              <button type="submit" disabled={busy || !code.trim()}
                className="w-full h-11 rounded-xl bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
                data-testid="twofa-verify-btn">
                {busy ? tf("Vérification…") : tf("Vérifier")}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
                  className="text-white/60 hover:text-white transition-colors" data-testid="twofa-toggle-recovery">
                  {useRecovery ? tf("Utiliser un code d'authentification") : tf("Utiliser un code de récupération")}
                </button>
                <button type="button" onClick={() => { setTwofa(null); setCode(""); setUseRecovery(false); }}
                  className="text-white/40 hover:text-white transition-colors" data-testid="twofa-cancel">
                  {tf("Annuler")}
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-[#C4D0E2]">
                {tf("Email")}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={tf("vous@exemple.com")}
                  className="pl-9 h-11 bg-white/[0.03] border-white/[0.14] text-white placeholder:text-[#64748B] focus-visible:border-white/30"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs text-[#C4D0E2]">
                  {tf("Mot de passe")}
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-[11px] text-[#93A5C0] hover:text-white transition-colors"
                  data-testid="login-forgot-link"
                >
                  {tf("Mot de passe oublié ?")}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" />
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 pr-10 h-11 bg-white/[0.03] border-white/[0.14] text-white placeholder:text-[#64748B] focus-visible:border-white/30"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md flex items-center justify-center text-[#7C8CA5] hover:text-white transition-colors"
                  aria-label={showPwd ? tf("Cacher") : tf("Afficher")}
                  data-testid="login-toggle-password"
                >
                  {showPwd ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 select-none cursor-pointer text-xs text-[#AEBDD4]">
              <Checkbox
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                className="border-white/30 data-[state=checked]:bg-[#0F172A] data-[state=checked]:border-[#3B5588] data-[state=checked]:text-white"
                data-testid="login-remember"
              />
              {tf("Se souvenir de moi")}
            </label>

            <Button
              type="submit"
              disabled={busy}
              className={cn(
                "w-full h-11 gap-2 active:scale-[0.98] transition-all duration-150",
                "bg-[#0F172A] text-white border border-[#26365A]",
                "hover:bg-[#152240] hover:border-[#33477A]",
                "focus-visible:ring-2 focus-visible:ring-white/35 focus-visible:ring-offset-2 focus-visible:ring-offset-[#080D15]",
                "disabled:opacity-60 disabled:text-white/70"
              )}
              data-testid="login-submit"
            >
              {busy ? tf("Connexion…") : (
                <>
                  {tf("Se connecter")}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </form>
          )}

          {!twofa && (
          <div className="mt-5 text-center text-xs text-[#93A5C0]">
            {tf("Pas encore de compte ?")}{" "}
            <Link
              to="/register"
              className="text-white font-medium hover:underline"
              data-testid="login-register-link"
            >
              {tf("Créer un compte")}
            </Link>
          </div>
          )}

          {/* Userflow trail */}
          <div className="hidden md:flex items-center justify-center gap-3 mt-10 text-[10px] uppercase tracking-[0.18em] text-[#8B9BB4]">
            <Step n={1} active label={tf("Se connecter")} />
            <Trail />
            <Step n={2} label={tf("Configurer")} />
            <Trail />
            <Step n={3} label={tf("Commencer")} />
          </div>
        </div>
      </main>
    </div>
  );
}

/* ====== icons & bits ====== */
function Step({ n, label, active }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold border",
          active
            ? "bg-white text-[#0A0C12] border-white"
            : "border-white/25 text-[#93A5C0]"
        )}
      >
        {n}
      </span>
      <span className={cn(active ? "text-white" : "text-[#93A5C0]")}>{label}</span>
    </div>
  );
}
function Trail() {
  return <div className="w-6 h-px bg-white/[0.15]" aria-hidden />;
}
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.83h5.36c-.23 1.4-1.66 4.1-5.36 4.1-3.22 0-5.85-2.66-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.06.78 3.76 1.45l2.56-2.46C16.79 3.71 14.6 2.7 12 2.7 6.86 2.7 2.7 6.86 2.7 12s4.16 9.3 9.3 9.3c5.36 0 8.91-3.77 8.91-9.07 0-.61-.07-1.07-.15-1.53H12z"/>
      <path fill="#34A853" d="M3.96 7.06l2.71 1.99C7.43 7.13 9.55 5.83 12 5.83c1.83 0 3.06.78 3.76 1.45l2.56-2.46C16.79 3.71 14.6 2.7 12 2.7 8.45 2.7 5.39 4.74 3.96 7.06z"/>
      <path fill="#FBBC05" d="M12 21.3c2.55 0 4.7-.84 6.27-2.29l-2.99-2.45c-.81.56-1.91 1-3.28 1-2.53 0-4.67-1.7-5.43-4.04l-2.79 2.16C5.36 19.27 8.41 21.3 12 21.3z"/>
      <path fill="#4285F4" d="M21.06 12c0-.61-.07-1.07-.15-1.53H12v3.83h5.36c-.27 1.6-1.7 3.2-4.36 3.5l3 2.46c1.78-1.65 2.86-4.07 2.86-7.06z"/>
    </svg>
  );
}
function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path fill="currentColor" d="M16.365 1.43c0 1.14-.402 2.18-1.207 3.12-.97 1.13-2.13 1.78-3.39 1.69-.07-1.1.41-2.21 1.18-3.1.81-.94 2.07-1.6 3.42-1.71zM20.45 17.6c-.55 1.27-.81 1.84-1.52 2.96-.99 1.55-2.39 3.49-4.13 3.5-1.55.02-1.95-1.01-4.06-1-2.11.01-2.55 1.02-4.1 1-1.74-.01-3.06-1.76-4.05-3.32-2.78-4.36-3.07-9.46-1.36-12.18 1.21-1.93 3.11-3.06 4.9-3.06 1.82 0 2.96.99 4.46.99 1.46 0 2.34-.99 4.45-.99 1.59 0 3.27.86 4.47 2.34-3.93 2.16-3.29 7.78.94 9.76z"/>
    </svg>
  );
}
